import React, { useState } from 'react';
import type { FC } from 'react';
import { Box, Spinner, useAsync } from '@wix/cli-ui-kit';
import { AuthProvider, SiteAuthProvider } from '@wix/cli-auth';
import { createPackageManager, getRepoType } from '@wix/package-manager';
import { globby } from 'globby';
import { match } from 'variant';
import { pathExists } from '@wix/cli-fs';
import { getWixConfigFilePath } from '@wix/cli-core-definitions';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { WelcomeMessage } from '../WelcomeMessage.js';
import { ChooseBusinessName } from '../ChooseBusinessName.js';
import { CreateBusiness } from '../CreateBusiness.js';
import { CreateProject } from '../CreateProject.js';
import { GenerateProject } from './GenerateProject.js';
import { FinishedSuccessfullyMessage } from './FinishedSuccessfullyMessage.js';
import type { ProjectData } from '../../hooks/create-project.js';

const BLANK_PROJECT_TEMPLATE_ID = '212b41cb-0da6-4401-9c72-7c579e6477a2';

function isWixConfigExists(projectFolder: string) {
  return pathExists(getWixConfigFilePath(projectFolder));
}

// https://docs.astro.build/en/basics/project-structure/#astroconfigmjs
async function hasAstroConfigurationFile(projectFolder: string) {
  const configFilesFound = await globby('astro.config.{js,cjs,mjs,ts}', {
    cwd: projectFolder,
    onlyFiles: true,
  });
  return configFilesFound.length === 1;
}

interface LinkCommandProps {
  projectFolder: string;
  businessName?: string;
}

export const LinkCommand: FC<LinkCommandProps> = ({
  projectFolder,
  businessName: preEnteredBusinessName,
}) => {
  const [businessName, setBusinessName] = useState(preEnteredBusinessName);
  const [businessId, setBusinessId] = useState<string>();
  const [projectData, setProjectData] = useState<ProjectData>();
  const [isDone, setIsDone] = useState(false);

  const { status } = useAsync(async () => {
    const alreadyLinked = await isWixConfigExists(projectFolder);
    if (alreadyLinked) {
      throw new CliError({
        code: CliErrorCode.ProjectIsAlreadyLinked(),
        cause: undefined,
      });
    }

    const isAstroProject = await hasAstroConfigurationFile(projectFolder);
    if (!isAstroProject) {
      throw new CliError({
        code: CliErrorCode.ProjectIsNotLinkable(),
        cause: undefined,
      });
    }

    const repoType = await getRepoType(projectFolder);
    const packageManager = await createPackageManager(repoType);

    return { repoType, packageManager };
  }, []);

  return match(status, {
    Error: () => null,
    Loading: () => <Spinner text="Loading..." />,
    Success: ({ result: { packageManager } }) => {
      return (
        <Box
          flexDirection="column"
          borderStyle="single"
          borderColor="gray"
          borderBottom={false}
          borderRight={false}
          borderTop={false}
          paddingLeft={1}
        >
          <WelcomeMessage />

          {!preEnteredBusinessName && (
            <ChooseBusinessName
              onSubmit={({ businessName }) => setBusinessName(businessName)}
            />
          )}

          {businessName && (
            <AuthProvider>
              <CreateBusiness
                businessName={businessName}
                businessTemplateId={BLANK_PROJECT_TEMPLATE_ID}
                onDone={({ businessId }) => setBusinessId(businessId)}
              />
            </AuthProvider>
          )}

          {businessName && businessId && (
            <SiteAuthProvider siteId={businessId}>
              <CreateProject
                businessId={businessId}
                businessName={businessName}
                onSubmit={(data) => setProjectData(data)}
              />
            </SiteAuthProvider>
          )}

          {businessName && businessId && projectData && (
            <GenerateProject
              packageManager={packageManager}
              projectFolder={projectFolder}
              businessId={businessId}
              projectData={projectData}
              onDone={() => setIsDone(true)}
            />
          )}

          {businessName && businessId && projectData && isDone && (
            <FinishedSuccessfullyMessage packageManager={packageManager} />
          )}
        </Box>
      );
    },
  });
};
